import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BookingStaffService, BookingLocation, BookingSyncAssignment } from './booking-staff.service';

interface LocationRow {
  locationId: string;
  name: string;
  checkControl: UntypedFormControl;
  roleControl: UntypedFormControl;
}

@Component({
  selector: 'app-booking-staff-sync-dialog',
  templateUrl: './booking-staff-sync-dialog.component.html',
  styleUrls: ['./booking-staff-sync-dialog.component.css'],
  standalone: false
})
export class BookingStaffSyncDialogComponent implements OnInit {
  loading = true;
  saving = false;
  rows: LocationRow[] = [];

  form = new UntypedFormGroup({
    factuName:   new UntypedFormControl(''),
    factuEmail:  new UntypedFormControl(''),
    bookitName:  new UntypedFormControl(''),
    bookitEmail: new UntypedFormControl('')
  });

  readonly roleOptions = [
    { value: 'staff',   label: 'Empleado' },
    { value: 'manager', label: 'Responsable' }
  ];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { employeeId: number; employeeName: string },
    private dialogRef: MatDialogRef<BookingStaffSyncDialogComponent>,
    private bookingStaffService: BookingStaffService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.bookingStaffService.getSyncStatus(this.data.employeeId).subscribe({
      next: (status) => {
        const factuName  = status.factuProfile?.name  || this.data.employeeName;
        const factuEmail = status.factuProfile?.email || '';

        this.form.patchValue({
          factuName,
          factuEmail,
          bookitName:  status.bookitProfile?.displayName || factuName,
          bookitEmail: status.bookitProfile?.email       || factuEmail
        });

        const activeIds = new Set(status.assignments.map(a => a.locationId));
        const titleMap  = new Map(status.assignments.map(a => [a.locationId, a.title ?? 'staff'] as [string, string]));

        this.rows = status.locations.map((loc: BookingLocation) => {
          const checked     = activeIds.has(loc.locationId);
          const roleControl = new UntypedFormControl({ value: titleMap.get(loc.locationId) || 'staff', disabled: !checked });
          const checkControl = new UntypedFormControl(checked);
          checkControl.valueChanges.subscribe((val: boolean) => val ? roleControl.enable() : roleControl.disable());
          return { locationId: loc.locationId, name: loc.name, checkControl, roleControl };
        });

        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.snackBar.open('Error al cargar los datos de Bookcit', 'Cerrar', { duration: 3000 });
      }
    });
  }

  copyNameToBookit():    void { this.form.get('bookitName').setValue(this.form.get('factuName').value); }
  copyNameFromBookit():  void { this.form.get('factuName').setValue(this.form.get('bookitName').value); }
  copyEmailToBookit():   void { this.form.get('bookitEmail').setValue(this.form.get('factuEmail').value); }
  copyEmailFromBookit(): void { this.form.get('factuEmail').setValue(this.form.get('bookitEmail').value); }

  save(): void {
    const assignments: BookingSyncAssignment[] = this.rows
      .filter(r => r.checkControl.value)
      .map(r => ({ locationId: r.locationId, title: r.roleControl.value }));

    this.saving = true;
    this.bookingStaffService.sync(this.data.employeeId, {
      assignments,
      displayName: this.form.get('bookitName').value  || undefined,
      email:       this.form.get('bookitEmail').value || undefined,
      factuName:   this.form.get('factuName').value   || undefined,
      factuEmail:  this.form.get('factuEmail').value  || undefined
    }).subscribe({
      next: () => {
        this.saving = false;
        this.snackBar.open('Empleado sincronizado con Bookcit', 'OK', { duration: 3000 });
        this.dialogRef.close(true);
      },
      error: () => {
        this.saving = false;
        this.snackBar.open('Error al sincronizar con Bookcit', 'Cerrar', { duration: 3000 });
      }
    });
  }

  cancel(): void { this.dialogRef.close(false); }
}
